// src/hooks/insights/useFirmwareLookupTable.ts
//
// Fetches the release-catalog lookup-table document (`getDFUInformation`) once on mount.
// Unlike `useInsightsData`/`useEarliestDataDate`, this catalog has no per-org or per-range
// parameter, so it is fetched eagerly rather than lazily and is never re-fetched when
// `range`/`preset` changes on the Insights page. A fetch failure is caught and logged rather
// than thrown into the render tree (matching `useEarliestDataDate`'s pattern) - callers treat
// a failed catalog fetch as "no currency data available," not a page-level error.

import { useEffect, useState } from 'react';
import { getDFUInformation } from '@/repository/DFUDAO';
import type { FirmwareLookupTableOnAzure } from '@/../generated-client/models';

export type UseFirmwareLookupTableResult = {
  data: FirmwareLookupTableOnAzure | null;
  loading: boolean;
  error: Error | null;
};

export function useFirmwareLookupTable(): UseFirmwareLookupTableResult {
  const [data, setData] = useState<FirmwareLookupTableOnAzure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    getDFUInformation().then(
      (result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      },
      (fetchError: unknown) => {
        console.error('useFirmwareLookupTable: getDFUInformation failed', fetchError);
        if (cancelled) return;
        setData(null);
        setError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
